import { useState, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from 'antd';
import { isMobile, isTablet } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { isMobileAction } from 'redux/device/device.action';
import { isEmpty } from 'lodash';
import { getCookie, setCookie } from 'helper/cookie';
import ModalLoading from 'common/ModalLoading';
import { BackgroundLoginPage, OverlayBackground } from 'common/background/background.common';
import AsideBarLayouts from 'components/Layouts/Asidebar/aside.layouts';
import HeaderLayouts from 'components/Layouts/Header/header.layouts';
import IndexPage from 'pages/Index/index.page.js';
import TemplateLimitPage from 'pages/TemplateLimit/templateLimit.page';
import TemplateLimitCreatePage from 'pages/TemplateLimit/templateLimit.create.page';

import TemplateFighthingPage from 'pages/TemplateFighthing/templateFighthing.page';
import TemplateFighthingCreatePage from 'pages/TemplateFighthing/templateFighthing.create.page';

import AutomaticallyLimitnumberPage from 'pages/AutomaticallyLimitnumber/automaticallyLimitnumber.page';
import AutomaticallyLimitnumberCreatePage from 'pages/AutomaticallyLimitnumber/automaticallyLimitnumber.create.page';

import ControlRatePage from 'pages/ControlRate/controlRate.page';
import ControlRateCreatePage from 'pages/ControlRate/controlRate.create.page';

import AnnounceOwnerCreatePage from 'pages/AnnounceOwner/announceOwner.page';

import DealerPage from 'pages/Dealer/dealer.page';

import { ContainerLogin, ContainerBackoffice } from './App.styles';
// import { WebSocketProvider } from './hooks/activeUser';
import useScrollPosition from './hooks/useScrollPosition';
// import { SSEProvider } from './hooks/activeUserSSE';
import { PollingProvider } from './hooks/activeUserLongPolling_bk';
import BannerOwnerPage from 'pages/BannerOwner/banner.page';
import CategoryTutorialCreatePage from 'pages/CategoryTutorial/categoryTutorial.create.page';
import CategoryTutorialPage from 'pages/CategoryTutorial/categoryTutorial.page';
import VideoTutorialPage from 'pages/VideoTutorial/videoTutorial.page';
import VideoTutorialCreatePage from 'pages/VideoTutorial/videoTutorial.create.page';
import TutorialPage from 'pages/Tutorial/tutorial.page';
import CustomerOwnerReportGovermentPage from 'pages/OwnerReportGoverment/ownerReportGoverment.page';

const { Content } = Layout;

const App = () => {
  const dispatch = useDispatch();
  const checkMobileView = isTablet || isMobile;
  dispatch(isMobileAction(checkMobileView));
  const [token, setToken] = useState<string>('');
  const [collapsed, setCollapsed] = useState<boolean>(checkMobileView ? true : false);
  const tokenCookie: string | undefined = getCookie('access_tokenltl');
  const authorization = !isEmpty(tokenCookie) ? tokenCookie : token;

  const { getScrollPosition, setScrollPosition } = useScrollPosition();

  useLayoutEffect(() => {
    const previousScrollPosition = getScrollPosition();
    setScrollPosition(previousScrollPosition);
  }, [getScrollPosition, setScrollPosition]);

  const HomePage = lazy(() => import('pages/Homepage/home.page'));
  const DashboardPage = lazy(() => import('pages/Dashboard/dashboard.page'));
  const ActiveUserPage = lazy(() => import('pages/ActiveUser/activeUser.page'));
  const ResourcePage = lazy(() => import('pages/Resource/resource.page'));
  const PermissionPage = lazy(() => import('pages/Permission/permission.page'));
  const PermissionCreatePage = lazy(() => import('pages/Permission/permission.create.page'));
  const RolesPage = lazy(() => import('pages/Roles/roles.page'));
  const RolesCreatePage = lazy(() => import('pages/Roles/roles.create.page'));
  const CustomerPage = lazy(() => import('pages/Customer/customer.page'));
  const CustomerCreatePage = lazy(() => import('pages/Customer/customer.create.page'));
  const ChangePasswordPage = lazy(() => import('pages/ChangPassword/customer.page'));
  const CategoryPage = lazy(() => import('pages/Category/category.page'));
  const CategoryCreatePage = lazy(() => import('pages/Category/category.create.page'));
  const LotteryTypePage = lazy(() => import('pages/LotteryType/lotteryType.page'));
  const LotteryTypeCreatePage = lazy(() => import('pages/LotteryType/lotteryType.create.page'));
  const GroupUserPage = lazy(() => import('pages/GroupUser/groupUser.page'));
  const GroupUserCreatePage = lazy(() => import('pages/GroupUser/groupUser.create.page'));
  const TypeNumberPage = lazy(() => import('pages/TypeNumber/typeNumber.page'));
  const TypeNumberCreatePage = lazy(() => import('pages/TypeNumber/typeNumber.create.page'));
  const BankAccountPage = lazy(() => import('pages/BankAccount/bankAccount.page'));
  const BankAccountCreatePage = lazy(() => import('pages/BankAccount/bankAccount.create.page'));
  const LevelPage = lazy(() => import('pages/Level/level.page'));
  const LevelCreatePage = lazy(() => import('pages/Level/level.create.page'));
  const PointPage = lazy(() => import('pages/Point/point.page'));
  const PointCreatePage = lazy(() => import('pages/Point/point.create.page'));
  const PromotionPage = lazy(() => import('pages/Promotion/promotion.page'));
  const PromotionCreatePage = lazy(() => import('pages/Promotion/promotion.create.page'));
  const TopupPage = lazy(() => import('pages/Topup/topup.page'));
  const TopupForeignPage = lazy(() => import('pages/TopupForeign/topupForeign.page'));
  const TopupStockPage = lazy(() => import('pages/TopupStock/topupStock.page'));
  const ReportTopupPage = lazy(() => import('pages/ReportTopup/reportTopup.page'));
  const MoneyTransferPage = lazy(() => import('pages/MoneyTransfer/moneyTransfer.page'));
  const MoneyTransferViewPage = lazy(() => import('pages/MoneyTransfer/moneyTransfer.view.page'));
  const ReportNotApprovedPage = lazy(() => import('pages/ReportNotApproved/reportNotApproved.page'));
  const ReportNotApprovedViewPage = lazy(() => import('pages/ReportNotApproved/reportNotApproved.view.page'));
  const ReportApprovedPage = lazy(() => import('pages/ReportApproved/reportApproved.page'));
  const ReportApprovedViewPage = lazy(() => import('pages/ReportApproved/reportApproved.view.page'));
  const PeriodPage = lazy(() => import('pages/Period/period.page'));
  const PeriodCreatePage = lazy(() => import('pages/Period/period.create.page'));
  const FighthingPage = lazy(() => import('pages/Fighthing/fighthing.page'));
  const FighthingDetail = lazy(() => import('components/Fighthing/fighthing.detail'));
  const LimitnumberPage = lazy(() => import('pages/Limitnumber/limitnumber.page'));
  const LimitnumberDetail = lazy(() => import('components/Limitnumber/limitnumber.detail'));
  const PayratePage = lazy(() => import('pages/Payrate/payrate.page'));
  const PayrateCreate = lazy(() => import('pages/Payrate/payrate.create.page'));
  const CustomerSeniorComponents = lazy(() => import('pages/CustomerSenior/customerSenior.page'));
  const CustomerSeniorCreatePage = lazy(() => import('pages/CustomerSenior/customerSenior.create.page'));
  const DetailCustomerSenior = lazy(() => import('components/CustomerSenior/detail/detail'));
  const CustomerMasterComponents = lazy(() => import('pages/CustomerMaster/customerMaster.page'));
  const CustomerMasterCreatePage = lazy(() => import('pages/CustomerMaster/customerMaster.create.page'));
  const DetailCustomerMaster = lazy(() => import('components/CustomerMaster/detail/detail'));
  const CustomerAgentComponents = lazy(() => import('pages/CustomerAgent/customerAgent.page'));
  const CustomerAgentCreatePage = lazy(() => import('pages/CustomerAgent/customerAgent.create.page'));
  const DetailCustomerAgent = lazy(() => import('components/CustomerAgent/detail/detail'));
  const CustomerMemberComponents = lazy(() => import('pages/CustomerMember/customerMember.page'));
  const CustomerMemberCreatePage = lazy(() => import('pages/CustomerMember/customerMember.create.page'));
  const DetailCustomerMember = lazy(() => import('components/CustomerMember/detail/detail'));
  const CustomerStaffComponents = lazy(() => import('pages/CustomerStaff/customerStaff.page'));
  const CustomerStaffCreatePage = lazy(() => import('pages/CustomerStaff/customerStaff.create.page'));
  const DetailCustomerStaff = lazy(() => import('components/CustomerStaff/detail/detail'));
  const TransferPointPage = lazy(() => import('pages/TransferPoint/transferPoint.page'));
  const HistoryTransferPointPage = lazy(() => import('pages/HistoryTransferPoint/historyTransferPoint.page'));
  const SetuptoFightPage = lazy(() => import('pages/SetuptoFight/setuptoFight.page'));
  const SetuptoDetailThrustin = lazy(() => import('components/SetuptoFight/Detail/thrustin'));
  const SetuptoDetailForward = lazy(() => import('components/SetuptoFight/Detail/forward'));
  const SetuptoDetailReceived = lazy(() => import('components/SetuptoFight/Detail/received'));
  const FullnumberPage = lazy(() => import('pages/Fullnumber/fullnumber.page'));
  const FullnumberDetail = lazy(() => import('components/Fullnumber/fullnumber.detail'));
  const ClearBalancePage = lazy(() => import('pages/ClearBalance/clearBalance.page'));
  const ClearBalanceDetail = lazy(() => import('components/ClearBalance/clearBalance.detail'));
  const BalancePage = lazy(() => import('pages/Balance/balance.page'));
  const BalanceDetail = lazy(() => import('components/Balance/balance.detail'));
  const AwardTypeFormatPage = lazy(() => import('pages/AwardTypeFormat/awardTypeFormat.page'));
  const AwardTypeFormatCreatePage = lazy(() => import('pages/AwardTypeFormat/awardTypeFormat.create.page'));
  const ManageAwardTypeFormatPage = lazy(() => import('pages/ManageAwardTypeFormat/ManageAwardTypeFormat.page'));
  const ManageAwardTypeFormatCreatePage = lazy(() => import('pages/ManageAwardTypeFormat/manageAwardTypeFormat.create.page'));

  const PrizeResultsPage = lazy(() => import('pages/PrizeResults/prizeResults.page'));
  const PrizeResultsDetail = lazy(() => import('components/PrizeResults/prizeResults.detail'));

  const EntranchToPlayPage = lazy(() => import('pages/EntranchToPlay/entranchToPlay.page'));
  const EntranchToPlayDetail = lazy(() => import('components/EntranchToPlay/Detail/entranch.detail'));

  const EntranchToPlayPageStaff = lazy(() => import('pages/EntranchToPlayStaff/entranchToPlayStaff.page'));
  const EntranchToPlayDetailStaff = lazy(() => import('components/EntranchToPlayStaff/Detail/entranchStaff.detail'));

  const CheckPayrate = lazy(() => import('pages/CheckPayrate/checkPayrate.page'));
  const CheckPriceSenior = lazy(() => import('components/CustomerSenior/CheckPrice/CheckPriceSenior.detail'));
  const CheckPriceMaster = lazy(() => import('components/CustomerMaster/CheckPrice/CheckPriceMaster.detail'));
  const CheckPriceAgent = lazy(() => import('components/CustomerAgent/CheckPrice/CheckPriceAgent.detail'));
  const CheckPriceMember = lazy(() => import('components/CustomerMember/CheckPrice/CheckPriceMember.detail'));

  const NotiDeletePoi = lazy(() => import('pages/NotiDeletePoi/notiDeletePoi.page'));

  const AwardsPage = lazy(() => import('pages/Awards/awards.page'));
  const AwardsDetail = lazy(() => import('components/Awards/awards.detail'));

  const ReportPage = lazy(() => import('pages/Report/report.page'));
  const ReportDetail = lazy(() => import('components/Report/report.detail'));
  const ReportMasterComponents = lazy(() => import('components/Report/ReportMaster/reportMaster.components'));
  const ReportInUserComponents = lazy(() => import('components/Report/ReportInUser/reportInUser.components'));

  const SummaryExpensesPage = lazy(() => import('pages/SummaryExpenses/summaryExpenses.page'));
  const SummaryExpensesMasterPage = lazy(() => import('pages/SummaryExpensesMaster/summaryExpensesMaster.page'));

  const ForwardingUserPage = lazy(() => import('pages/ForwardingUser/forwardingUser.page'));
  const ForwardingUserCreatePage = lazy(() => import('pages/ForwardingUser/forwardingUser.create.page'));
  const ForwardingDetailCustomer = lazy(() => import('components/ForwardingUser/detail/detail'));
  const AutomaticallyLimitnumberDetail = lazy(() => import('components/AutomaticallyLimitnumber/automaticallyLimitnumber.detail'));

  const EntranchToPlayPageDealer = lazy(() => import('pages/EntranchToPlayDealer/entranchToPlayDealer.page'));
  const EntranchToPlayDetailDealer = lazy(() => import('components/EntranchDealer/Detail/entranchDealer.detail'));

  const AwardsDealerPage = lazy(() => import('pages/AwardsDealer/awardsDealer.page'));
  const AwardsDealerDetail = lazy(() => import('components/AwardsDealer/awardsDealer.detail'));

  const ForwardingDealerPage = lazy(() => import('pages/Forwarding/forwarding.page'));
  const ForwardingDealerDetail = lazy(() => import('components/Forwarding/forwarding.detail'));

  const ForwardingManualPage = lazy(() => import('pages/ForwardingManual/forwardingManual.page'));
  const ForwardingManualDetail = lazy(() => import('components/ForwardingManual/forwardingManual.detail'));

  const SummaryDealerPage = lazy(() => import('pages/SummaryDealer/summaryDealer.page'));
  const SummaryMyGroupPage = lazy(() => import('pages/SummaryMyGroup/summaryMyGroup.page'));

  const CustomerOwnerPage = lazy(() => import('pages/OwnerInformation/ownerInformation.page'));
  const CustomerOwnerInactivePage = lazy(() => import('pages/OwnerInactive/ownerInactive.page'));
  const CustomerOwnerTopup = lazy(() => import('pages/OwnerTopup/ownerTopup.page'));
  const CustomerOwnerReport = lazy(() => import('pages/OwnerReport/ownerReport.page'));

  const ReportInOwnerPage = lazy(() => import('pages/ReportInOwner/reportInOwner.page'));
  const ReportInOwnerDetail = lazy(() => import('components/ReportInOwner/reportInOwner.detail'));

  const AwardsStaffPage = lazy(() => import('pages/AwardsStaff/awardsStaff.page'));
  const AwardsStaffDetail = lazy(() => import('components/AwardsStaff/awardsStaff.detail'));

  const setTokenData = (token: string) => {
    setToken(token);
  };
  useEffect(() => {
    if (authorization && token === null) {
      setTokenData(authorization);
    }
    return () => {
      authorization;
    };
  }, [tokenCookie, token, authorization]);

  const setSideBarToggle = (value: boolean) => {
    setCollapsed(value);
  };

  const chechMediaQuery = () => {
    if (checkMobileView) {
      setCookie('widthMobile', String(window?.innerWidth));
      setCookie('heightMobile', String(window?.innerHeight));
    }
  };

  useEffect(() => {
    if (checkMobileView) {
      chechMediaQuery();
    }
  }, [chechMediaQuery, checkMobileView]);

  // console.log('asdsa', getCookie('widthMobile'))

  const checkAuthLoginRoute = () => {
    if (authorization) {
      return (
        <PollingProvider authorization={authorization}>
          <ContainerBackoffice style={{ width: checkMobileView ? '0px' : '99.01vw' }}>
            <Layout>
              <AsideBarLayouts collapsed={collapsed} setTokenData={setTokenData} setSideBarToggle={setSideBarToggle} />
              <Layout
                className="site-layout"
                style={{
                  background: checkMobileView ? '' : '',
                  width: checkMobileView ? '' : '100%',
                }}
              >
                <HeaderLayouts collapsed={collapsed} setSideBarToggle={setSideBarToggle} />
                <Content
                  className="site-layout-background"
                  style={{
                    margin: checkMobileView ? '10px 0px' : '10px 10px',
                    padding: checkMobileView ? '0px' : '10px',
                    minHeight: checkMobileView ? `${Number(getCookie('heightMobile')) - 7}px` : 700,
                    marginLeft: checkMobileView ? '' : '250px',
                    minWidth: checkMobileView ? `${Number(getCookie('widthMobile')) - 7}px` : '390px',
                  }}
                >
                  <Suspense fallback={<ModalLoading open={true} />}>
                    <Routes>
                      <Route path="/backoffice" element={<HomePage />} />
                      <Route path="/backoffice/homepage" element={<HomePage />} />
                      <Route path="/backoffice/dashboard" element={<DashboardPage />} />
                      <Route path="/backoffice/active_user" element={<ActiveUserPage />} />
                      <Route path="/backoffice/resource" element={<ResourcePage />} />
                      <Route path="/backoffice/permission" element={<PermissionPage />} />
                      <Route path="/backoffice/permission/create" element={<PermissionCreatePage />} />
                      <Route path="/backoffice/permission/edit/:id" element={<PermissionCreatePage />} />
                      <Route path="/backoffice/roles" element={<RolesPage />} />
                      <Route path="/backoffice/roles/create" element={<RolesCreatePage />} />
                      <Route path="/backoffice/roles/edit/:id" element={<RolesCreatePage />} />
                      <Route path="/backoffice/roles/permission/:id" element={<RolesCreatePage />} />
                      <Route path="/backoffice/customer" element={<CustomerPage />} />
                      <Route path="/backoffice/customer/create" element={<CustomerCreatePage />} />
                      <Route path="/backoffice/customer/edit/:id" element={<CustomerCreatePage />} />
                      <Route path="/backoffice/changepassword" element={<ChangePasswordPage />} />
                      <Route path="/backoffice/category" element={<CategoryPage />} />
                      <Route path="/backoffice/category/create" element={<CategoryCreatePage />} />
                      <Route path="/backoffice/category/edit/:id" element={<CategoryCreatePage />} />
                      <Route path="/backoffice/category_tutorial" element={<CategoryTutorialPage />} />
                      <Route path="/backoffice/category_tutorial/create" element={<CategoryTutorialCreatePage />} />
                      <Route path="/backoffice/category_tutorial/edit/:id" element={<CategoryTutorialCreatePage />} />
                      <Route path="/backoffice/video_tutorial" element={<VideoTutorialPage />} />
                      <Route path="/backoffice/video_tutorial/create" element={<VideoTutorialCreatePage />} />
                      <Route path="/backoffice/video_tutorial/edit/:id" element={<VideoTutorialCreatePage />} />
                      <Route path="/backoffice/tutorial" element={<TutorialPage />} />
                      <Route path="/backoffice/lottery_type" element={<LotteryTypePage />} />
                      <Route path="/backoffice/lottery_type/create" element={<LotteryTypeCreatePage />} />
                      <Route path="/backoffice/lottery_type/edit/:id" element={<LotteryTypeCreatePage />} />
                      <Route path="/backoffice/group_user" element={<GroupUserPage />} />
                      <Route path="/backoffice/group_user/create" element={<GroupUserCreatePage />} />
                      <Route path="/backoffice/group_user/edit/:id" element={<GroupUserCreatePage />} />
                      <Route path="/backoffice/type_number" element={<TypeNumberPage />} />
                      <Route path="/backoffice/type_number/create" element={<TypeNumberCreatePage />} />
                      <Route path="/backoffice/type_number/edit/:id" element={<TypeNumberCreatePage />} />
                      <Route path="/backoffice/bank_account" element={<BankAccountPage />} />
                      <Route path="/backoffice/bank_account/create" element={<BankAccountCreatePage />} />
                      <Route path="/backoffice/bank_account/edit/:id" element={<BankAccountCreatePage />} />
                      <Route path="/backoffice/level" element={<LevelPage />} />
                      <Route path="/backoffice/level/create" element={<LevelCreatePage />} />
                      <Route path="/backoffice/level/edit/:id" element={<LevelCreatePage />} />
                      <Route path="/backoffice/point" element={<PointPage />} />
                      <Route path="/backoffice/point/create" element={<PointCreatePage />} />
                      <Route path="/backoffice/point/edit/:id" element={<PointCreatePage />} />
                      <Route path="/backoffice/promotion" element={<PromotionPage />} />
                      <Route path="/backoffice/promotion/create" element={<PromotionCreatePage />} />
                      <Route path="/backoffice/promotion/edit/:id" element={<PromotionCreatePage />} />
                      <Route path="/backoffice/topup" element={<TopupPage />} />
                      <Route path="/backoffice/topup_foreign" element={<TopupForeignPage />} />
                      <Route path="/backoffice/topup_stock" element={<TopupStockPage />} />
                      <Route path="/backoffice/report_topup" element={<ReportTopupPage />} />
                      <Route path="/backoffice/transfer_point" element={<TransferPointPage />} />
                      <Route path="/backoffice/history_transfer_point" element={<HistoryTransferPointPage />} />
                      <Route path="/backoffice/money_transfer" element={<MoneyTransferPage />} />
                      <Route path="/backoffice/money_transfer/view/:id" element={<MoneyTransferViewPage />} />
                      <Route path="/backoffice/report_not_approved" element={<ReportNotApprovedPage />} />
                      <Route path="/backoffice/report_not_approved/view/:id" element={<ReportNotApprovedViewPage />} />
                      <Route path="/backoffice/report_approved" element={<ReportApprovedPage />} />
                      <Route path="/backoffice/report_approved/view/:id" element={<ReportApprovedViewPage />} />
                      <Route path="/backoffice/period" element={<PeriodPage />} />
                      <Route path="/backoffice/period/create" element={<PeriodCreatePage />} />
                      <Route path="/backoffice/period/edit/:id" element={<PeriodCreatePage />} />

                      <Route path="/backoffice/template_limit" element={<TemplateLimitPage />} />
                      <Route path="/backoffice/template_limit/create" element={<TemplateLimitCreatePage />} />
                      <Route path="/backoffice/template_limit/edit/:id" element={<TemplateLimitCreatePage />} />

                      <Route path="/backoffice/fighthing" element={<FighthingPage />} />
                      <Route path="/backoffice/fighthing/detail/:periodId" element={<FighthingDetail />} />

                      <Route path="/backoffice/template_fighthing" element={<TemplateFighthingPage />} />
                      <Route path="/backoffice/template_fighthing/create" element={<TemplateFighthingCreatePage />} />
                      <Route path="/backoffice/template_fighthing/edit/:id" element={<TemplateFighthingCreatePage />} />

                      <Route path="/backoffice/limitnumber" element={<LimitnumberPage />} />
                      <Route path="/backoffice/limitnumber/detail/:periodId" element={<LimitnumberDetail />} />

                      <Route path="/backoffice/fullnumber" element={<FullnumberPage />} />
                      <Route path="/backoffice/fullnumber/detail/:periodId" element={<FullnumberDetail />} />
                      <Route path="/backoffice/payrate" element={<PayratePage />} />
                      <Route path="/backoffice/payrate/create" element={<PayrateCreate />} />
                      <Route path="/backoffice/payrate/edit/:groupId" element={<PayrateCreate />} />

                      <Route path="/backoffice/control_rate" element={<ControlRatePage />} />
                      <Route path="/backoffice/control_rate/create" element={<ControlRateCreatePage />} />
                      <Route path="/backoffice/control_rate/edit/:id" element={<ControlRateCreatePage />} />

                      <Route path="/backoffice/setuptofight" element={<SetuptoFightPage />} />
                      <Route path="/backoffice/setuptofight/detail/:id" element={<SetuptoDetailThrustin />} />
                      <Route path="/backoffice/setuptofight/detail/:id/thrustin" element={<SetuptoDetailThrustin />} />
                      <Route path="/backoffice/setuptofight/detail/:id/forward" element={<SetuptoDetailForward />} />
                      <Route path="/backoffice/setuptofight/detail/:id/received" element={<SetuptoDetailReceived />} />

                      <Route path="/backoffice/clear_balance" element={<ClearBalancePage />} />
                      <Route path="/backoffice/clear_balance/detail/:periodId" element={<ClearBalanceDetail />} />

                      <Route path="/backoffice/balance" element={<BalancePage />} />
                      <Route path="/backoffice/balance/detail/:periodId" element={<BalanceDetail />} />

                      <Route path="/backoffice/award_type_format" element={<AwardTypeFormatPage />} />
                      <Route path="/backoffice/award_type_format/create" element={<AwardTypeFormatCreatePage />} />
                      <Route path="/backoffice/award_type_format/edit/:id" element={<AwardTypeFormatCreatePage />} />

                      <Route path="/backoffice/manage_award_type_format" element={<ManageAwardTypeFormatPage />} />
                      <Route path="/backoffice/manage_award_type_format/create" element={<ManageAwardTypeFormatCreatePage />} />
                      <Route path="/backoffice/manage_award_type_format/edit/:id" element={<ManageAwardTypeFormatCreatePage />} />

                      <Route path="/backoffice/prize_results" element={<PrizeResultsPage />} />
                      <Route path="/backoffice/prize_results/detail/:periodId" element={<PrizeResultsDetail />} />

                      <Route path="/backoffice/entranch_to_play" element={<EntranchToPlayPage />} />
                      <Route path="/backoffice/entranch_to_play/detail/:id/:lotterytypeId" element={<EntranchToPlayDetail />} />

                      <Route path="/backoffice/entranch_to_play_staff" element={<EntranchToPlayPageStaff />} />
                      <Route path="/backoffice/entranch_to_play_staff/detail/:id/:lotterytypeId" element={<EntranchToPlayDetailStaff />} />

                      <Route path="/backoffice/member_senior" element={<CustomerSeniorComponents />} />
                      <Route path="/backoffice/member_senior/create" element={<CustomerSeniorCreatePage />} />
                      <Route path="/backoffice/member_senior/edit/:id" element={<CustomerSeniorCreatePage />} />
                      <Route path="/backoffice/member_senior/detail/:id" element={<DetailCustomerSenior />} />
                      <Route path="/backoffice/member_senior/check_price_detail/:type" element={<CheckPriceSenior />} />

                      <Route path="/backoffice/member_master" element={<CustomerMasterComponents />} />
                      <Route path="/backoffice/member_master/create" element={<CustomerMasterCreatePage />} />
                      <Route path="/backoffice/member_master/edit/:id" element={<CustomerMasterCreatePage />} />
                      <Route path="/backoffice/member_master/detail/:id" element={<DetailCustomerMaster />} />
                      <Route path="/backoffice/member_master/check_price_detail/:type" element={<CheckPriceMaster />} />

                      <Route path="/backoffice/member_agent" element={<CustomerAgentComponents />} />
                      <Route path="/backoffice/member_agent/create" element={<CustomerAgentCreatePage />} />
                      <Route path="/backoffice/member_agent/edit/:id" element={<CustomerAgentCreatePage />} />
                      <Route path="/backoffice/member_agent/detail/:id" element={<DetailCustomerAgent />} />
                      <Route path="/backoffice/member_agent/check_price_detail/:type" element={<CheckPriceAgent />} />

                      <Route path="/backoffice/member_member" element={<CustomerMemberComponents />} />
                      <Route path="/backoffice/member_member/create" element={<CustomerMemberCreatePage />} />
                      <Route path="/backoffice/member_member/edit/:id" element={<CustomerMemberCreatePage />} />
                      <Route path="/backoffice/member_member/detail/:id" element={<DetailCustomerMember />} />
                      <Route path="/backoffice/member_member/check_price_detail/:type" element={<CheckPriceMember />} />

                      <Route path="/backoffice/member_staff" element={<CustomerStaffComponents />} />
                      <Route path="/backoffice/member_staff/create" element={<CustomerStaffCreatePage />} />
                      <Route path="/backoffice/member_staff/edit/:id" element={<CustomerStaffCreatePage />} />
                      <Route path="/backoffice/member_staff/detail/:id" element={<DetailCustomerStaff />} />

                      <Route path="/backoffice/check_payrate" element={<CheckPayrate />} />
                      <Route path="/backoffice/noti_delete_poit" element={<NotiDeletePoi />} />

                      <Route path="/backoffice/awards" element={<AwardsPage />} />
                      <Route path="/backoffice/awards/detail/:periodId" element={<AwardsDetail />} />

                      <Route path="/backoffice/report" element={<ReportPage />} />
                      <Route path="/backoffice/report/detail/:periodId" element={<ReportDetail />} />

                      <Route path="/backoffice/report/detail/:periodId/:masterId" element={<ReportMasterComponents />} />
                      <Route path="/backoffice/report/detail/:periodId/:masterId/:inUserId" element={<ReportInUserComponents />} />

                      <Route path="/backoffice/summary_expenses" element={<SummaryExpensesPage />} />
                      <Route path="/backoffice/summary_expenses_master" element={<SummaryExpensesMasterPage />} />

                      <Route path="/backoffice/forwarding_user" element={<ForwardingUserPage />} />
                      <Route path="/backoffice/forwarding_user/create" element={<ForwardingUserCreatePage />} />
                      <Route path="/backoffice/forwarding_user/edit/:id" element={<ForwardingUserCreatePage />} />
                      <Route path="/backoffice/forwarding_user/detail/:id" element={<ForwardingDetailCustomer />} />

                      <Route path="/backoffice/automatically_limitnumber" element={<AutomaticallyLimitnumberPage />} />
                      <Route path="/backoffice/automatically_limitnumber/create" element={<AutomaticallyLimitnumberCreatePage />} />
                      <Route path="/backoffice/automatically_limitnumber/edit/:id" element={<AutomaticallyLimitnumberCreatePage />} />
                      <Route path="/backoffice/automatically_limitnumber/detail/:id" element={<AutomaticallyLimitnumberDetail />} />

                      <Route path="/backoffice/announce_owner" element={<AnnounceOwnerCreatePage />} />

                      <Route path="/backoffice/banner_owner" element={<BannerOwnerPage />} />

                      <Route path="/backoffice/dealer" element={<DealerPage />} />

                      <Route path="/backoffice/entranch_to_play_dealer" element={<EntranchToPlayPageDealer />} />
                      <Route path="/backoffice/entranch_to_play_dealer/detail/:lotterytypeId" element={<EntranchToPlayDetailDealer />} />

                      <Route path="/backoffice/awards_dealer" element={<AwardsDealerPage />} />
                      <Route path="/backoffice/awards_dealer/detail/:lotterytypeId" element={<AwardsDealerDetail />} />

                      <Route path="/backoffice/forwarding" element={<ForwardingDealerPage />} />
                      <Route path="/backoffice/forwarding/detail/:periodId" element={<ForwardingDealerDetail />} />

                      <Route path="/backoffice/forwarding_manual" element={<ForwardingManualPage />} />
                      <Route path="/backoffice/forwarding_manual/detail/:periodId" element={<ForwardingManualDetail />} />

                      <Route path="/backoffice/summary_dealer" element={<SummaryDealerPage />} />
                      <Route path="/backoffice/summary_member_group" element={<SummaryMyGroupPage />} />
                      <Route path="/backoffice/owner_information" element={<CustomerOwnerPage />} />
                      <Route path="/backoffice/owner_inactive" element={<CustomerOwnerInactivePage />} />
                      <Route path="/backoffice/owner_topup" element={<CustomerOwnerTopup />} />
                      <Route path="/backoffice/owner_report" element={<CustomerOwnerReport />} />
                      <Route path="/backoffice/owner_report_goverment" element={<CustomerOwnerReportGovermentPage />} />
                      <Route path="/backoffice/report_in_owner" element={<ReportInOwnerPage />} />
                      <Route path="/backoffice/report_in_owner/detail/:id" element={<ReportInOwnerDetail />} />

                      <Route path="/backoffice/awards_staff" element={<AwardsStaffPage />} />
                      <Route path="/backoffice/awards_staff/detail/:periodId" element={<AwardsStaffDetail />} />
                      <Route path="*" element={<Navigate to="/backoffice" />} />
                    </Routes>
                  </Suspense>
                </Content>
              </Layout>
            </Layout>
          </ContainerBackoffice>
        </PollingProvider>
      );
    } else {
      return (
        <ContainerLogin>
          <BackgroundLoginPage />
          <OverlayBackground />
          <Routes>
            <Route path="/" element={<IndexPage setTokenData={setTokenData} />} />
            <Route path="/index" element={<IndexPage setTokenData={setTokenData} />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </ContainerLogin>
      );
    }
  };

  return checkAuthLoginRoute();
};

export default App;
